/**
 * MKS-464 — Projects view of the Delivery tab: only projects with a delivery, an in-progress
 * task or hours in the period (the server filters), each expandable to its delivered tasks by
 * day and its in-progress tasks with their assignees.
 */
import { useState } from 'react';
import { TeamAvatars } from './TeamAvatars';
import { DeliveryTaskList } from './DeliveryTaskList';
import type { DeliveryProjectRow } from '../../services/api/delivery';
import { formatDisplayDateTime, formatHours } from '../../utils/format';

const GRID = 'grid grid-cols-[1fr_90px_90px_80px_130px_150px]';
const DASH = '—';

export interface DeliveryProjectsTableProps {
  projects: DeliveryProjectRow[];
}

export function DeliveryProjectsTable({ projects }: DeliveryProjectsTableProps) {
  const [open, setOpen] = useState<Set<number>>(new Set());

  function toggle(id: number) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (projects.length === 0) {
    return (
      <div className="bg-ms-surface border border-ms-border-subtle rounded-lg p-8 text-center text-[12.5px] text-ms-text-secondary">
        No project activity in this period.
      </div>
    );
  }

  return (
    <div className="bg-ms-surface border border-ms-border-subtle rounded-lg overflow-hidden text-[12.5px]">
      <div
        className={`${GRID} sticky top-0 bg-ms-surface-2 border-b border-ms-border-subtle text-[10.5px] uppercase tracking-wider font-medium text-ms-text-muted`}
      >
        <div className="px-4 py-2">Project</div>
        <div className="px-2 py-2 text-right">Delivered</div>
        <div className="px-2 py-2 text-right">In progress</div>
        <div className="px-2 py-2 text-right">Hours</div>
        <div className="px-4 py-2">People</div>
        <div className="px-4 py-2">Last delivery</div>
      </div>
      {projects.map((project) => {
        const expanded = open.has(project.id);
        const panelId = `delivery-panel-project-${project.id}`;
        return (
          <div key={project.id} className="border-b border-ms-border-subtle">
            <button
              type="button"
              className={`${GRID} w-full items-center text-left hover:bg-ms-surface-2/40 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ms-orange/40`}
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => toggle(project.id)}
            >
              <span className="px-4 py-2.5 font-medium text-ms-text-strong truncate">
                {project.name}
              </span>
              <span className="px-2 py-2.5 text-right tabular-nums">{project.delivered}</span>
              <span className="px-2 py-2.5 text-right tabular-nums">{project.inProgress}</span>
              <span className="px-2 py-2.5 text-right tabular-nums">
                {formatHours(project.hours)}
              </span>
              <span className="px-4 py-2.5">
                <TeamAvatars team={project.people} max={5} />
              </span>
              <span className="px-4 py-2.5 text-ms-text-secondary">
                {project.lastDeliveryAt ? formatDisplayDateTime(project.lastDeliveryAt) : DASH}
              </span>
            </button>
            {expanded && (
              <div id={panelId} className="px-4 pb-3 pt-1 bg-ms-surface-2/20">
                <DeliveryTaskList
                  deliveredTasks={project.deliveredTasks}
                  inProgressTasks={project.inProgressTasks}
                  showAssignee
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
